<template>
  <div
    v-on-click-outside="() => (dropdownOpen = false)"
    class="relative flex min-w-max"
  >
    <button
      class="relative grow transition-colors"
      :class="[
        buttonClasses,
        buttonOptions?.class,
        toggleOptions?.hidden ? 'rounded-sm' : 'rounded-l-sm',
        {
          'cursor-not-allowed': disabled,
        },
      ]"
      data-cy="primary-button-with-dropdown"
      :disabled="disabled"
      @click="emit('button:click')"
    >
      <div class="flex items-center px-3">
        <div
          class="flex space-x-2 font-semibold"
          :class="variant === 'link' ? 'text-primary' : 'text-white'"
        >
          <slot name="button" />
        </div>
      </div>
    </button>
    <div
      v-if="!toggleOptions?.hidden"
      v-tooltip="toggleOptions?.tooltip"
      class="flex shrink-0 cursor-pointer items-center justify-center rounded-r-sm transition-colors"
      :class="[toggleClasses, toggleOptions?.class]"
      data-cy="dropdown-button-toggle"
      @click="toggleDropdown"
    >
      <slot name="toggle-icon">
        <dropdown-chevron
          class="mt-0 mr-0 cursor-pointer"
          :class="{ 'cursor-default!': toggleOptions?.disabled }"
          data-cy="dropdown-options-chevron"
          :is-open="dropdownOpen"
          :is-default-position="false"
          :fill="variant === 'link' ? 'currentColor' : 'white'"
        />
      </slot>
    </div>
    <template v-if="dropdownOpen">
      <slot v-if="$slots.dropdown" name="dropdown" :close="closeDropdown" />
      <ul v-else class="action-menu-dd top-full right-0 mt-2">
        <action-menu-item
          v-for="item in dropdownItems"
          :key="item.label"
          @click.stop="handleActionMenuItemClick(item.handler)"
        >
          <component
            :is="item.componentData.component"
            v-if="item.componentData"
            v-bind="item.componentData.props"
            @change="item.handler"
          >
            <span :data-cy="`dropdown-item--${item.label}`">
              {{ item.label }}
            </span>
          </component>
          <div v-else class="flex items-center space-x-2">
            <icon-base v-if="item.icon" :icon="item.icon" class="mr-2" />
            <span>{{ item.label }}</span>
          </div>
        </action-menu-item>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import ActionMenuItem from "@/components/ui/ActionMenuItem.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IDropdownItem } from "@/models/common";

interface ButtonOptions {
  class?: string;
}

interface ToggleOptions {
  class?: string;
  tooltip?: string;
  externalOnly?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

const emit = defineEmits<{
  "button:click": [];
  "toggle:dropdown": [];
}>();

const {
  buttonOptions,
  disabled,
  dropdownItems = [],
  toggleOptions,
  variant = "primary",
} = defineProps<{
  buttonOptions?: ButtonOptions;
  disabled?: boolean;
  dropdownItems?: IDropdownItem[];
  toggleOptions?: ToggleOptions;
  variant?: "primary" | "link";
}>();

const dropdownOpen = ref(false);

const buttonClasses = computed(() => {
  if (variant === "link") {
    return "bg-transparent shadow-none hover:opacity-80";
  }
  return "h-10 bg-primary bg-linear-to-bl from-primary-button-1 to-primary-button-2 shadow-primary-button hover:to-primary-button-hover-2";
});

const toggleClasses = computed(() => {
  if (variant === "link") {
    return [
      "w-8 text-primary",
      {
        "opacity-50 cursor-default!": toggleOptions?.disabled,
        "hover:bg-gray-100": !toggleOptions?.disabled,
      },
    ];
  }
  return [
    "h-10 w-10",
    {
      "bg-blue-400! cursor-default!": toggleOptions?.disabled,
      "bg-primary hover:bg-blue-800": !dropdownOpen.value,
      "bg-blue-800": dropdownOpen.value,
    },
  ];
});

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const handleActionMenuItemClick = (callback: () => void) => {
  dropdownOpen.value = false;
  callback();
};

const toggleDropdown = () => {
  if (toggleOptions?.disabled) {
    return;
  }

  if (!toggleOptions?.externalOnly) {
    dropdownOpen.value = !dropdownOpen.value;
  }
  emit("toggle:dropdown");
};

defineExpose({
  dropdownOpen,
});
</script>
